import React, { useEffect, useState } from "react";

import { useTranslation } from "utils";

import { AntModal, Button, Dot, Icon, Typography } from "components";

interface CropDetectionModalProps {
  isVisible: boolean;
  progress: number;
  startYear?: number;
  endYear?: number;
  onDone: () => void;
}

const SIMULATION_DURATION_MS = 30_000;
const SIMULATION_MAX = 66;
const STEP_THRESHOLDS = [0, 22, 44, 66];

type StepState = "pending" | "in-progress" | "complete";

const getStepState = (index: number, displayProgress: number, isDone: boolean): StepState => {
  if (isDone) return "complete";
  const nextThreshold = STEP_THRESHOLDS[index + 1];
  if (nextThreshold !== undefined && displayProgress >= nextThreshold) return "complete";
  if (displayProgress >= STEP_THRESHOLDS[index]) return "in-progress";
  return "pending";
};

const renderStepIcon = (state: StepState) => {
  if (state === "complete") {
    return <Icon iconName="tabler/circle-check-filled" size="s" className="crop-detection-modal-step-icon crop-detection-modal-step-icon--complete" />;
  }
  if (state === "in-progress") {
    return <Icon iconName="tabler/loader" size="s" className="crop-detection-modal-step-icon crop-detection-modal-step-icon--progress anticon-spin" />;
  }
  return <span className="crop-detection-modal-step-icon crop-detection-modal-step-icon--pending" aria-hidden="true" />;
};

export const CropDetectionModal = ({ isVisible, progress, startYear, endYear, onDone }: CropDetectionModalProps) => {
  const { t } = useTranslation("onboarding");
  const [simProgress, setSimProgress] = useState(0);

  const isDone = progress >= 100;
  const displayProgress = isDone ? 100 : Math.max(simProgress, progress);

  const hasYearData = startYear !== undefined && endYear !== undefined;
  const yearsCount = hasYearData ? endYear - startYear + 1 : null;

  useEffect(() => {
    if (!isVisible) {
      setSimProgress(0);
      return;
    }
    const startTime = Date.now();
    const id = setInterval(() => {
      const fraction = Math.min((Date.now() - startTime) / SIMULATION_DURATION_MS, 1);
      setSimProgress(Math.floor(fraction * SIMULATION_MAX));
      if (fraction >= 1) clearInterval(id);
    }, 200);
    return () => clearInterval(id);
  }, [isVisible]);

  const steps = [
    t("cropDetectionModal.step1", "Validating fields and boundaries"),
    hasYearData
      ? t("cropDetectionModal.step2", "Detecting crop rotation {{startYear}}–{{endYear}}", { startYear, endYear })
      : t("cropDetectionModal.step2Pending", "Detecting crop rotation"),
    t("cropDetectionModal.step3", "Detecting cover crops"),
    t("cropDetectionModal.step4", "Deriving geospatial data for your fields"),
  ];

  return (
    <AntModal
      open={isVisible}
      footer={null}
      closable={false}
      centered
      width={480}
      classNames={{ mask: "crop-detection-modal-mask" }}
      className="crop-detection-modal-ant"
      data-testid="crop-detection-modal"
    >
      <div className="crop-detection-modal-content">
        <div className="crop-detection-modal-progress-section">
          <div className="crop-detection-modal-progress-header">
            <div className="crop-detection-modal-progress-icon-wrapper">
              <Icon iconName="tabler/stack" size="m" className="crop-detection-modal-logo-icon" />
              <Dot color="#9bd39a" size={8} className="crop-detection-modal-progress-dot" />
            </div>
            <Typography variant="h3" className="crop-detection-modal-progress-headline">
              {t("cropDetectionModal.progressHeadline", "Analysing your fields")}
            </Typography>
          </div>
          <div className="crop-detection-modal-progress-bar-track">
            <div className="crop-detection-modal-progress-bar-fill" style={{ transform: `scaleX(${displayProgress / 100})` }} />
          </div>
          <div className="crop-detection-modal-progress-footer">
            <Typography variant="paragraph-s" className="crop-detection-modal-progress-sub">
              {t("cropDetectionModal.progressSub", "Building your crop rotation baseline")}
            </Typography>
            <Typography variant="paragraph-s" className="crop-detection-modal-progress-percent">
              {displayProgress}%
            </Typography>
          </div>
        </div>

        <div className="crop-detection-modal-results-section">
          <Typography variant="h3" className="crop-detection-modal-results-headline">
            {yearsCount !== null
              ? t("cropDetectionModal.resultsHeadline", {
                  count: yearsCount,
                  defaultValue_one: "{{count}} year of crop history detected automatically — no manual entry needed.",
                  defaultValue_other: "{{count}} years of crop history detected automatically — no manual entry needed.",
                })
              : t("cropDetectionModal.resultsHeadlinePending", "Crop history detected automatically — no manual entry needed.")}
          </Typography>
          <ul className="crop-detection-modal-steps">
            {steps.map((step, index) => {
              const state = getStepState(index, displayProgress, isDone);
              return (
                <li key={step} className="crop-detection-modal-step">
                  {renderStepIcon(state)}
                  <Typography variant="paragraph-m" className={`crop-detection-modal-step-text crop-detection-modal-step-text--${state}`}>
                    {step}
                  </Typography>
                </li>
              );
            })}
          </ul>
          <Button type="primary" disabled={!isDone} onClick={onDone} data-testid="crop-detection-modal-done-btn" className="crop-detection-modal-done-btn">
            {t("cropDetectionModal.cta", "Done")}
          </Button>
          <Typography variant="paragraph-s" color="grey-600" className="crop-detection-modal-timing">
            {t("cropDetectionModal.timing", "Takes up to 2 minutes")}
          </Typography>
        </div>
      </div>
    </AntModal>
  );
};

export default CropDetectionModal;
